/**
 * MONSTER TOWN — TOWN JOBS, FINDS AND DAILY GIFTS. OWNED BY THE ECONOMY CREW.
 *
 * The three ways coins arrive when you are NOT running the shop:
 *
 *  1. THE JOB BOARD. Tap it and up to three things you already built start
 *     sparkling. Tap each one and it pays; clear them all and there is a bonus.
 *     It is a scavenger hunt through your own town, it needs no reading, and it
 *     cannot be failed — a job with nothing to mark still pays out.
 *  2. FINDS. Every so often a fat coin appears somewhere walkable in the town.
 *     Tap it, it is yours. This is the "there is always something out there"
 *     drip that makes wandering worth it.
 *  3. THE DAILY GIFT. Come back after a while and there is a present waiting.
 *     Time only ever GIVES in this game; nothing decays while you are away.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { wallet } from '../core/economy'
import { world } from '../core/scene'
import type { Cell } from '../core/grid'
import type { InstanceId, PlaceableId, SceneId } from '../core/types'
import { place, removeInstance } from '../build/placer'
import { ball, cylinder, group } from '../render/kit'
import { findCoin, jobBoard } from '../content/venue'

const TOWN = 'town' as SceneId
/** Never mark a door or a coin — a job must point at things you BUILT. */
const NEVER_MARK: ReadonlySet<string> = new Set<string>([
  jobBoard.id,
  findCoin.id,
  'venue_shopfront',
  'shop_front',
  'venue_exit',
  'shop_plinth',
])

const MAX_MARKS = 3
const MAX_FINDS = 3
const FIND_EVERY = 22

interface Mark {
  sceneId: SceneId
  instanceId: InstanceId
  art: THREE.Group
  t: number
}

const marks: Mark[] = []
let findTimer = FIND_EVERY

function markArt(): THREE.Group {
  const g = group('jobMark')
  const ring = cylinder(0.55, 0.06, { color: 'uiCoin', seg: 14, name: 'ring' })
  g.add(ring)
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2
    g.add(ball(0.11, { color: 'flowerWhite', x: Math.cos(a) * 0.55, y: 0.2, z: Math.sin(a) * 0.55, seg: 6 }))
  }
  g.add(ball(0.18, { color: 'uiCoinDark', y: 0.36, seg: 8 }))
  return g
}

export function activeJobMarks(): number {
  return marks.length
}

/** Hand out a job. Always succeeds, always pays something. */
export function startJob(sceneId: SceneId): number {
  const node = world.get(sceneId)
  if (node === undefined) return 0
  clearMarks()

  const candidates = node.grid.all().filter((p) => !NEVER_MARK.has(p.defId))
  // Seeded shuffle so a drive run is reproducible.
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(engine.rng() * (i + 1))
    const a = candidates[i]
    const b = candidates[j]
    if (a !== undefined && b !== undefined) {
      candidates[i] = b
      candidates[j] = a
    }
  }

  const mark = (instanceId: InstanceId): void => {
    const p = node.grid.placement(instanceId)
    if (p === null) return
    const centre = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
    const art = markArt()
    art.position.set(centre.x, centre.y + 0.1, centre.z)
    node.actors.add(art)
    marks.push({ sceneId, instanceId, art, t: 0 })
  }

  for (const p of candidates.slice(0, MAX_MARKS)) mark(p.instanceId)

  // A BRAND NEW TOWN HAS NOTHING BUILT YET, and a job that marks nothing is a
  // dead end. Top the job up with fresh coins scattered in the grass, so the
  // hunt always exists: three sparkling things to go and find, every time.
  while (marks.length < MAX_MARKS) {
    const before = node.grid.all().length
    if (!dropFind(sceneId)) break
    const added = node.grid.all().find((p) => p.defId === findCoin.id && !marks.some((m) => m.instanceId === p.instanceId))
    if (added === undefined || node.grid.all().length === before) break
    mark(added.instanceId)
  }

  if (marks.length === 0) {
    // Truly nowhere to put anything? Still pay — never a dead end (LAW #2).
    const paid = wallet.earn('town.job')
    bus.emit('economy:jobDone', { paid })
    bus.emit('ui:toast', { icon: 'coin' })
    return 0
  }
  bus.emit('economy:jobStarted', { marks: marks.length })
  bus.emit('audio:cue', { cue: 'job_start' })
  return marks.length
}

function clearMarks(): void {
  for (const m of marks) m.art.removeFromParent()
  marks.length = 0
}

/** Returns true if this tap collected a marked job step. */
export function collectMark(sceneId: SceneId, instanceId: InstanceId): boolean {
  const i = marks.findIndex((m) => m.sceneId === sceneId && m.instanceId === instanceId)
  if (i < 0) return false
  const m = marks[i]
  if (m === undefined) return false
  const at = { x: m.art.position.x, y: m.art.position.y, z: m.art.position.z }
  m.art.removeFromParent()
  marks.splice(i, 1)
  wallet.earn('town.visitor', at)
  bus.emit('audio:cue', { cue: 'coin' })
  if (marks.length === 0) {
    const paid = wallet.earn('town.job', at)
    bus.emit('economy:jobDone', { paid })
    bus.emit('audio:cue', { cue: 'chime_happy' })
    bus.emit('ui:toast', { icon: 'star' })
  }
  return true
}

export function updateMarks(dt: number): void {
  for (const m of marks) {
    m.t += dt
    m.art.rotation.y += dt * 1.6
    m.art.position.y = 0.1 + Math.abs(Math.sin(m.t * 2.2)) * 0.35
  }
}

// ---------------------------------------------------------------------------
// coins in the grass
// ---------------------------------------------------------------------------

function countFinds(sceneId: SceneId): number {
  const node = world.get(sceneId)
  if (node === undefined) return 0
  return node.grid.all().filter((p) => p.defId === findCoin.id).length
}

/** Drop one coin somewhere walkable and empty. Silent: not an undo step. */
export function dropFind(sceneId: SceneId): boolean {
  const node = world.get(sceneId)
  if (node === undefined) return false
  const g = node.grid
  for (let tries = 0; tries < 16; tries++) {
    const gx = Math.floor(engine.rng() * g.spec.width)
    const gz = Math.floor(engine.rng() * g.spec.depth)
    const cell: Cell = { gx, gy: 0, gz }
    if (!g.inBounds(cell) || g.at(cell) !== null) continue
    const id = place(sceneId, findCoin.id as PlaceableId, cell, 0, { silent: true })
    if (id !== null) return true
  }
  return false
}

export function updateFinds(dt: number): void {
  const sceneId = world.activeSceneId
  if (sceneId === null) return
  const node = world.require(sceneId)
  if (node.def.kind !== 'exterior') return
  findTimer -= dt
  if (findTimer > 0) return
  findTimer = FIND_EVERY
  if (countFinds(sceneId) >= MAX_FINDS) return
  dropFind(sceneId)
}

/** The coin's own `coins` feedback already paid; this just tidies it away. */
export function takeFind(sceneId: SceneId, instanceId: InstanceId): void {
  removeInstance(sceneId, instanceId, true)
}

// ---------------------------------------------------------------------------
// daily return
// ---------------------------------------------------------------------------

const SIX_HOURS = 6 * 60 * 60 * 1000

/**
 * Award the come-back-tomorrow gift. `lastSeenAt` is 0 on a brand new town, in
 * which case there is nothing to welcome back yet.
 */
export function awardDailyGift(lastSeenAt: number, now: number): number {
  if (lastSeenAt === 0 || now - lastSeenAt < SIX_HOURS) return 0
  const a = wallet.earn('daily.return')
  const b = wallet.earn('daily.gift')
  bus.emit('ui:toast', { icon: 'gift', ms: 2600 })
  bus.emit('audio:cue', { cue: 'chime_happy' })
  return a + b
}

export function installJobs(): void {
  bus.on('play:interacted', (e) => {
    if (e.defId === jobBoard.id) {
      startJob(e.sceneId)
      return
    }
    // Collect the job mark FIRST: a marked coin has to pay for both the find
    // and the job step before it is tidied away.
    collectMark(e.sceneId, e.instanceId)
    if (e.defId === findCoin.id) takeFind(e.sceneId, e.instanceId)
  })

  // Marks belong to a scene's actors layer, which is never saved: drop them
  // when the scene changes or a different kid's town is loaded.
  bus.on('scene:left', () => clearMarks())
  bus.on('save:loaded', () => clearMarks())
}

export { TOWN as TOWN_SCENE }
